import { useState } from 'react';
import { Copy, Check, ExternalLink, Wallet, CreditCard, Download } from 'lucide-react';
import { useChainId } from 'wagmi';
import { base } from 'wagmi/chains';
import { Button } from '@/components/ui/button';
import { useWallet } from '@/hooks/useWallet';
import { useUSDCBalance } from '@/hooks/useEscrow';
import { formatAddress } from '@/lib/utils';

export function WalletPanel() {
  const { address, ethBalance, fundWallet, exportWallet, isConnected } = useWallet();
  const { balance: usdcBalance, isLoadingBalance } = useUSDCBalance();
  const chainId = useChainId();
  const [copied, setCopied] = useState(false);

  if (!isConnected || !address) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No wallet connected
      </div>
    );
  }

  const explorerBase = chainId === base.id
    ? 'https://basescan.org'
    : 'https://sepolia.basescan.org';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formattedUsdc = usdcBalance != null
    ? parseFloat(usdcBalance).toFixed(2)
    : '—';

  const formattedEth = parseFloat(ethBalance).toFixed(4);

  return (
    <div className="w-72 p-4 space-y-4">
      {/* Address */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-mono">{formatAddress(address, 6)}</span>
        </div>
        <button
          onClick={handleCopy}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      {/* Balances */}
      <div className="space-y-2 rounded-lg bg-muted/50 p-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">USDC</span>
          <span className="text-sm font-semibold">
            {isLoadingBalance ? '...' : `$${formattedUsdc}`}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">ETH</span>
          <span className="text-sm font-medium text-muted-foreground">
            {formattedEth} ETH
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <Button
          variant="default"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={fundWallet}
        >
          <CreditCard className="h-4 w-4" />
          Add Funds
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={exportWallet}
        >
          <Download className="h-4 w-4" />
          Export Wallet
        </Button>
        <a
          href={`${explorerBase}/address/${address}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          View on Explorer
        </a>
      </div>
    </div>
  );
}
